import jQuery from 'jquery';
import dialogPolyfill from 'dialog-polyfill';
import type { SbDialogWrapper } from '../../modules/web-components/sb-dialog-wrapper';

const focusableSelector = 'a:not([disabled]), button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), summary';

const headerCloseButtonHTML = `
    <div class="dialog-header-buttons">
        <button type="button" class="dialog-close" title="Close"><i class="far fa-times fa-2x" aria-hidden="true"></i></button>
    </div>`;

const footerCloseButtonHTML = `<button type="button" class="btn btn-default dialog-close">Cancel</button>`;

export interface DialogTemplateOptions {
  /** The id to apply to the wrapping <sb-dialog-wrapper> element */
  wrapperId: string;
  /** The id to give the <dialog> element. */
  dialogId?: string;
  /** Will be added as the class attribute of the <dialog> element */
  dialogClasses?: string;
  /** Include the close button with the X in the header? Default is false. */
  hideCloseButtonInHeader?: boolean;
  /** Include the Cancel button in the footer? Default is false. */
  hideCloseButtonInFooter?: boolean;
  /** What to put in the dialog's <h2>. Default is ''. */
  title?: string;
  /** Set to true if you don't want a <h2 class="dialog-title"> element */
  noTitle?: boolean;
  /** Whatever you want inserted as innerHTML for the dialog-body element. Default is ''. */
  content?: string;
}

/** The SiteBuilderDialog is intended as an accessible replacement for our old Bootstrap modals.
 *  A full example HTML dialog template is as follows:
 *
 *  <dialog>
 *    <div class="dialog-content">
 *      <div class="dialog-header">
 *        <h2 class="dialog-title">[Your title here]</h2>
 *        <div class="dialog-header-buttons">
 *          <button type="button" class="dialog-close" title="Close">
 *            <i class="far fa-times fa-2x" aria-hidden="true"></i>
 *          </button>
 *        </div>
 *      </div>
 *      <div class="dialog-body">
 *        [Your HTML content here]
 *      </div>
 *      <div class="dialog-footer">
 *        <button type="button" class="btn btn-default dialog-close">Cancel</button>
 *      </div>
 *    </div>
 *  </dialog>
 *
 *  .dialog-header and .dialog-footer and all their contents are optional
 *  Any element with the .dialog-close class will be set up to close the dialog on click.
 *  Pressing escape will always close the dialog, unless it has the data-no-esc attribute set to 'true'.
 *  The dialog will trap focus and not allow tabbing to the content behind it.
 *  You can automate an open button by giving the <dialog> an id, and setting the trigger's aria-controls attribute to that id
 */
export class SiteBuilderDialog {
  /** The original HTML element passed to the constructor */
  dialogElement: HTMLDialogElement;
  /** Will be used to track addition/removal of elements inside the <dialog> element */
  private mutationObserver: MutationObserver;
  /** An array of all focusable elements inside the main <dialog> element */
  private focusableElements: HTMLElement[];

  constructor(dialogElement: HTMLDialogElement) {
    this.dialogElement = dialogElement;
    if (SiteBuilderDialog.isAlreadyRegistered(dialogElement)) {
      // If it's already stamped with data-registered-dialog we don't need to set this stuff up again
      return;
    }

    if (!dialogElement.showModal) {
      dialogPolyfill.registerDialog(dialogElement);
    }

    if (this.dialogElement.parentElement.tagName !== 'BODY') {
      // SBTWO-10571 <dialog> elements have to go in the <body> element for the dialog-polyfill to work nicely
      document.body.appendChild(this.dialogElement);
    }

    this.updateFocusableElements();

    // Refresh the focusable nodes list whenever nodes are added/removed from the <dialog> element
    this.mutationObserver = new MutationObserver(() => {
      this.updateFocusableElements();
    });

    this.mutationObserver.observe(dialogElement, {subtree: true, childList: true, attributes: true});

    dialogElement.addEventListener('click', (event) => {
      if (event.composedPath().find((target) => {
        return target && (target as HTMLElement).classList?.contains('dialog-close')
      }) !== undefined) {
        this.close();
      }
    });

    dialogElement.addEventListener('keydown', (event) => {
      if ((event.key === 'Esc' || event.key === 'Escape')) {
        event.preventDefault();
        event.stopPropagation();
        if (this.dialogElement.dataset.noEsc !== 'true') {
          this.close();
        }
      }
      if (event.key === 'Tab') {
        // Trap focus to this element while it's visible
        event.preventDefault();
        this.updateFocusableElements(); // Check we haven't lost focusable elements through DOM confusion
        const targets = this.focusableElements;
        const currentFocusIndex = targets
          .findIndex((element) => element === document.activeElement) ?? 0;
        if (event.shiftKey) {
          if (currentFocusIndex === 0) {
            targets[targets.length - 1]?.focus();
          } else {
            targets[currentFocusIndex - 1]?.focus();
          }
        } else {
          if (currentFocusIndex === targets.length - 1) {
            targets[0]?.focus();
          } else {
            targets[currentFocusIndex + 1]?.focus()
          }
        }
      }
    });

    if (this.dialogElement.id?.trim() !== '') {
      document.querySelectorAll(`button[aria-controls="${this.dialogElement.id}"], a[aria-controls="${this.dialogElement.id}"]`).forEach((openButton) => {
        openButton.addEventListener('click', (event) => {
          event.preventDefault();
          this.open();
        });
      });
    }

    this.handleDatePickers();

    this.dialogElement.dataset.registeredDialog = 'true';
  }

  /** Gets an array of all focusable elements inside the main <dialog> element */
  private updateFocusableElements() {
    // Looks for elements matching the selector above, that are still rendered somewhere, but aren't empty <a> tags, which sometimes crop up and break things
    this.focusableElements = Array.from(this.dialogElement.querySelectorAll(focusableSelector))
      .filter((el) => (el.checkVisibility?.() ?? true) && (el as HTMLElement).offsetParent !== null && (el.childNodes.length > 0 || el.tagName !== 'A')) as HTMLElement[];
  }

  /** Called in the constructor to handle ab-datepickers which could otherwise end up disappearing off the side/bottom
   * of the dialog */
  private handleDatePickers() {
    // Filthy jQuery befouling my lovely new typescript. Sadly this is the simplest way to intercept one of its
    // stupid custom events which it doesn't see fit to trigger as a normal event for normal JS people (ಥ﹏ಥ)
    jQuery(this.dialogElement).on('ab.datepicker.opened', (event) => {
      const picker = event.target as HTMLElement;
      const targetInput = this.dialogElement.querySelector(`#${picker.getAttribute('aria-controls')}`) as HTMLElement;

      this.dialogElement.appendChild(picker);
      const inputRect = targetInput.getBoundingClientRect();
      const dialogRect = this.dialogElement.getBoundingClientRect();
      picker.style.left = `${inputRect.left - dialogRect.left}px`;
      if (picker.classList.contains('above')) {
        // The datepicker library decided the best position is above the element, so let's honour that
        const top = (inputRect.top - dialogRect.top - picker.clientHeight);
        // Avoid pushing it off the top of the viewport
        const clampedTop = Math.max(-dialogRect.top, top);
        picker.style.top = `${clampedTop}px`;
      } else {
        const max = (window.innerHeight - dialogRect.top) - picker.clientHeight;
        const bottom = (inputRect.bottom - dialogRect.top);
        const clampedBottom = Math.min(max, bottom);
        picker.style.top = `${clampedBottom}px`;
      }
    });
  }

  /** Show the dialog if it's not already open */
  open() {
    if (!this.dialogElement.hasAttribute('open')) {
      this.dialogElement.showModal();
    }
  }

  /** Hide the dialog */
  close() {
    this.dialogElement.close();
  }

  /** Completely remove the <dialog> from the HTML */
  delete() {
    this.mutationObserver.disconnect();
    this.dialogElement.remove();
  }

  static isAlreadyRegistered(dialogElement: HTMLDialogElement | null): boolean {
    return dialogElement?.dataset?.registeredDialog === 'true';
  }

  /** Use this to make sure all buttons/anchors that have an aria-controls pointing to a <dialog> control that dialog */
  static updateDialogTriggers() {
    SiteBuilderDialog.whenWrapperDefined(() => {
      document.querySelectorAll(('button[aria-controls], a[aria-controls]')).forEach((button) => {
        const controlId = button.getAttribute('aria-controls');
        const possibleDialog = document.querySelector(`dialog#${controlId}`);
        if (possibleDialog) {
          button.addEventListener('click', () => {
            (possibleDialog as HTMLDialogElement).showModal();
          });
        }
      });
    });
  }

  /** Returns a <dialog> element with header, body, footer and close buttons ready for you to work with */
  static blankDialog(options?: DialogTemplateOptions): SbDialogWrapper {
    const dialogHtml = `
      <sb-dialog-wrapper id="${options?.wrapperId}">
        <dialog${options.dialogId ? ` id="${options.dialogId}"` : ''}${options.dialogClasses ? ` class="${options.dialogClasses}"` : ''}>
          <div class="dialog-content">
            <div class="dialog-header">
              ${options?.noTitle ? '' : `<h2 class="dialog-title">${options.title ?? ''}</h2>`}
              ${options?.hideCloseButtonInHeader ? '' : headerCloseButtonHTML }
            </div>
            <div class="dialog-body">
              ${options?.content ?? ''}
            </div>
            <div class="dialog-footer">
              ${options?.hideCloseButtonInFooter ? '' : footerCloseButtonHTML }
            </div>
          </div>
        </dialog>
      </sb-dialog-wrapper>
    `;
    const domParser = new DOMParser();
    return domParser.parseFromString(dialogHtml, 'text/html').querySelector('sb-dialog-wrapper') as SbDialogWrapper;
  }

  static whenWrapperDefined(fn: () => void) {
    window.customElements.whenDefined('sb-dialog-wrapper').then(() => {
      fn();
    });
  }
}
